import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { getApiClient } from 'api';
import {
  getFitPlayerProfilesByIdUserInfo,
  getUsersById,
  putUsersMarketConditionAccepted,
  type PutUsersMarketConditionAcceptedResponse,
  type GetFitPlayerProfilesByIdUserInfoResponse,
  type GetUsersByIdResponse,
} from 'api/generated';
import { currentUser, setCurrentUser, getAPIToken } from 'helpers/authentication';
import type { FollowUnfollowParameters } from 'store/followUnfollow/followUnfollowSlice';
import { setFollowed, setUnfollowed } from 'store/followUnfollow/followUnfollowSlice';

const FOLLOWABLE_TYPE = 'user';

export const fetchPlayerUserProfile = createAsyncThunk<
  GetFitPlayerProfilesByIdUserInfoResponse | undefined,
  number | string | undefined
>('fit-player-profiles/user-info/fetchPlayerUserProfile', async (playerId, { rejectWithValue }) => {
  try {
    if (!playerId) return undefined;

    const response = await getFitPlayerProfilesByIdUserInfo({
      client: getApiClient(true),
      path: { id: Number(playerId) },
      throwOnError: true,
    });

    return response.data;
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const fetchUserProfile = createAsyncThunk<GetUsersByIdResponse | undefined, number | string | undefined>(
  'users/fetchUserProfile',
  async (userId, { rejectWithValue }) => {
    try {
      if (!userId) return undefined;

      const response = await getUsersById({
        client: getApiClient(true),
        path: { id: String(userId) },
        throwOnError: true,
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const acceptMarketCondition = createAsyncThunk<PutUsersMarketConditionAcceptedResponse>(
  'users/market-condition-accepted/acceptMarketCondition',
  async (_, { rejectWithValue }) => {
    try {
      const response = await putUsersMarketConditionAccepted({
        client: getApiClient(),
        headers: { 'X-Api-Key': getAPIToken() ?? '' },
        throwOnError: true,
      });

      return response.data as PutUsersMarketConditionAcceptedResponse;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState: {
    error: false,
    loading: false,
    user: undefined as GetFitPlayerProfilesByIdUserInfoResponse | undefined,
  },
  reducers: {
    clearUserProfile: (state) => {
      state.user = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPlayerUserProfile.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(
      fetchPlayerUserProfile.fulfilled,
      (state, action: PayloadAction<GetFitPlayerProfilesByIdUserInfoResponse | undefined>) => {
        state.loading = false;
        state.user = action.payload;
      },
    );
    builder.addCase(fetchPlayerUserProfile.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(fetchUserProfile.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(
      fetchUserProfile.fulfilled,
      (state, action: PayloadAction<GetFitPlayerProfilesByIdUserInfoResponse | undefined>) => {
        state.loading = false;
        state.user = action.payload;
      },
    );
    builder.addCase(fetchUserProfile.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(setFollowed, (state, action: PayloadAction<FollowUnfollowParameters>) => {
      const { id, type } = action.payload;
      if (type === FOLLOWABLE_TYPE && state.user && state.user.id === id) {
        state.user.followed = true;
      }
    });
    builder.addCase(setUnfollowed, (state, action: PayloadAction<FollowUnfollowParameters>) => {
      const { id, type } = action.payload;
      if (type === FOLLOWABLE_TYPE && state.user && state.user.id === id) {
        state.user.followed = false;
      }
    });
    builder.addCase(acceptMarketCondition.pending, (state) => {
      state.loading = true;
      state.error = false;
    });
    builder.addCase(acceptMarketCondition.fulfilled, (state) => {
      state.loading = false;
      setCurrentUser({
        ...currentUser(),
        market_condition_accepted: true,
      });
    });
    builder.addCase(acceptMarketCondition.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
  },
});

export const { clearUserProfile } = userProfileSlice.actions;
export const userPreviewReducer = userProfileSlice.reducer;
